import React, { useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import Input from '../Inputs/Input';
import {
  calculateRollCount,
  getFitment,
} from '../../Services/WallpaperCalculatorService';
import Dropdown from '../Dropdown';
import { ListItem } from '../../Types/ListItem';
import WallpaperCalculatorResult from './WallpaperCalculatorResult';
import Listbox from '../Listbox';

interface WallpaperCalculator {
  productPatternMatches: ListItem[];
}

const WallpaperCalculator = ({
  productPatternMatches,
}: WallpaperCalculator) => {
  const [wallWidth, setWallWidth] = useState<number | undefined>(4.5);
  const [wallHeight, setWallHeight] = useState<number | undefined>(2.4);
  const [rollWidth, setRollWidth] = useState<number | undefined>(0.53);
  const [rollLength, setRollLength] = useState<number | undefined>(10.05);
  const [patternSize, setPatternSize] = useState<number | undefined>(0.53);
  const [fitment, setFitment] = useState<number>(0);
  const [fitmentOptionListOpen, setFitmentOptionListOpen] =
    useState<boolean>(false);
  const calculatorResult = useMemo(
    () =>
      wallWidth && wallHeight && rollWidth && rollLength
        ? calculateRollCount({
            wallWidth,
            wallHeight,
            rollWidth,
            rollLength,
            patternSize: patternSize ?? 0.01,
            fitment,
          })
        : undefined,
    [wallWidth, wallHeight, rollWidth, rollLength, patternSize, fitment]
  );

  const fitmentOptionList = useMemo(
    () =>
      productPatternMatches.map((p) => ({
        label: p.text,
        value: getFitment(p.value ?? '1').toString(),
        selected:
          fitment.toString() === getFitment(p.value ?? '1').toString(),
      })),
    [productPatternMatches]
  );

  return (
    <div className="mt-6">
      <h2 className="mb-3">{translate('wallpaper-calculator.title')}</h2>
      <form>
        <Input
          label={`${translate('wallpaper-calculator.wall-width')} (m)`}
          className="mb-4"
          type="number"
          step="0.01"
          value={wallWidth ?? ''}
          onChange={(e) =>
            setWallWidth(
              e.target.value != '' ? parseFloat(e.target.value) : undefined
            )
          }
          showAnimatedLabel={true}
          inputSize="large"
        />
        <Input
          label={`${translate('wallpaper-calculator.wall-height')} (m)`}
          className="mb-4"
          type="number"
          step="0.01"
          value={wallHeight ?? ''}
          onChange={(e) =>
            setWallHeight(
              e.target.value != '' ? parseFloat(e.target.value) : undefined
            )
          }
          showAnimatedLabel={true}
          inputSize="large"
        />
        <Input
          label={`${translate('wallpaper-calculator.roll-width')} (m)`}
          className="mb-4"
          type="number"
          step="0.01"
          value={rollWidth ?? ''}
          onChange={(e) =>
            setRollWidth(
              e.target.value != '' ? parseFloat(e.target.value) : undefined
            )
          }
          showAnimatedLabel={true}
          inputSize="large"
        />
        <Input
          label={`${translate('wallpaper-calculator.roll-length')} (m)`}
          className="mb-4"
          type="number"
          step="0.01"
          value={rollLength ?? ''}
          onChange={(e) =>
            setRollLength(
              e.target.value != '' ? parseFloat(e.target.value) : undefined
            )
          }
          showAnimatedLabel={true}
          inputSize="large"
        />
        {fitment > 0 && (
          <Input
            label={`${translate('wallpaper-calculator.pattern-size')} (m)`}
            className="mb-4"
            type="number"
            step="0.01"
            value={patternSize ?? ''}
            onChange={(e) =>
              setPatternSize(
                e.target.value != ''
                  ? parseFloat(e.target.value)
                  : undefined
              )
            }
            showAnimatedLabel={true}
            inputSize="large"
          />
        )}
        <Dropdown
          options={productPatternMatches.map((p) => ({
            label: p.text,
            value: getFitment(p.value ?? '1').toString(),
          }))}
          selectedValue={fitment}
          onChange={({ value }) => setFitment(parseFloat(value))}
          label={translate('wallpaper-calculator.fitment')}
          className="mb-4"
          hideForDesktop={true}
        />
        <Listbox
          optionList={fitmentOptionList}
          selectedOption={fitmentOptionList.find(
            (o) => o.value == fitment.toString()
          )}
          setSelectedOption={(option) =>
            setFitment(parseFloat(option.value ?? '0'))
          }
          isOpen={fitmentOptionListOpen}
          setIsOpen={setFitmentOptionListOpen}
          label={translate('wallpaper-calculator.fitment')}
          inputSize="large"
          showStickyLabel={true}
        />
      </form>
      {typeof calculatorResult?.rolls !== 'undefined' ? (
        <WallpaperCalculatorResult
          {...calculatorResult}
          showWaste={fitment > 0}
        />
      ) : (
        <span>{translate('wallpaper-calculator.fill-in-form')}</span>
      )}
    </div>
  );
};

export default WallpaperCalculator;
